import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Monitor, Zap, Smartphone, GraduationCap } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useActiveSection } from "@/hooks/useActiveSection";
import { useLocation, useNavigate } from "react-router-dom";
import logoLmd from "@/assets/Logo_AiLum_Solutions.webp";
import logoLmd300 from "@/assets/Logo_AiLum_Solutions-300w.webp";

const sectionIds = ["sluzby", "spoluprace", "o-nas", "kontakt"];

const serviceDropdownItems = [
  { icon: Monitor, label: { cs: "Tvorba webových stránek", en: "Website Development" }, href: "/tvorba-webu", color: "#00D4C8" },
  { icon: Zap, label: { cs: "AI automatizace procesů", en: "AI Process Automation" }, href: "/ai-automatizace", color: "rgb(16, 185, 129)" },
  { icon: Smartphone, label: { cs: "Tvorba aplikací", en: "Application Development" }, href: "/tvorba-aplikaci", color: "#8B5CF6" },
  { icon: GraduationCap, label: { cs: "AI školení a workshopy", en: "AI Training & Workshops" }, href: "/ai-skoleni", color: "#F97316" },
];

const serviceSubpages = ["/tvorba-webu", "/ai-automatizace", "/tvorba-aplikaci", "/ai-skoleni"];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const { language, setLanguage, t } = useLanguage();
  const activeSection = useActiveSection(sectionIds);
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isHomepage = location.pathname === "/";
  const isServiceSubpage = serviceSubpages.includes(location.pathname);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 100);
  };

  const updateDropdownPos = () => {
    if (headerRef.current && wrapperRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({
        top: headerRect.bottom,
        left: wrapperRect.left,
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      if (isDropdownOpen) updateDropdownPos();
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDropdownOpen]);

  useEffect(() => {
    window.addEventListener("resize", updateDropdownPos);
    return () => window.removeEventListener("resize", updateDropdownPos);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedHeader = headerRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);
      if (!clickedHeader && !clickedDropdown) {
        setIsDropdownOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (isHomepage && location.hash) {
      const sectionId = location.hash.replace("#", "");
      const timeout = setTimeout(() => {
        scrollToSection(sectionId);
      }, 120);
      return () => clearTimeout(timeout);
    }
  }, [location, isHomepage]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("/#")) {
      e.preventDefault();
      const sectionId = href.replace("/#", "");
      if (isHomepage) {
        scrollToSection(sectionId);
      } else {
        navigate("/" + "#" + sectionId);
      }
    }
  };

  const handleServicesClick = () => {
    if (isHomepage) {
      scrollToSection("sluzby");
    } else {
      navigate("/#sluzby");
    }
  };

  const isActive = (href: string) => {
    if (href === "#sluzby") {
      return isHomepage ? activeSection === "sluzby" : isServiceSubpage;
    }
    const sectionId = href.replace("/#", "").replace("#", "");
    return isHomepage && activeSection === sectionId;
  };

  const servicesItem = t.nav.items[0];
  const regularNavItems = t.nav.items.slice(1);

  const dropdownPortal = createPortal(
    <AnimatePresence>
      {isDropdownOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          // Dropdown clearuje timeout — cursor může přijet z libovolné strany
          onMouseEnter={clearCloseTimeout}
          onMouseLeave={scheduleClose}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: "280px",
            zIndex: 9999,
            backgroundColor: isScrolled ? "rgba(7, 10, 19, 0.8)" : "rgb(7, 10, 19)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            borderTop: "none",
            borderLeft: "none",
            borderRight: "none",
            borderBottom: "1px solid rgba(51, 51, 51, 0.5)",
            borderRadius: "0.3rem",
          }}
        >
          <div className="py-2">
            {serviceDropdownItems.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className="flex items-center gap-3 px-4 py-3 transition-colors duration-200"
                  style={{ color: "rgba(255,255,255,0.8)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.10)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                  }}
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <Icon size={18} style={{ color: item.color }} />
                  <span className="text-sm font-medium">
                    {item.label[language]}
                  </span>
                </a>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );

  return (
    <>
      <motion.header
        ref={headerRef}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass-nav" : "max-lg:bg-black lg:bg-transparent"
        }`}
        // KLÍČOVÉ: header clearuje timeout — dropdown zůstane otevřený
        // dokud je cursor kdekoliv na navbaru
        onMouseEnter={clearCloseTimeout}
        onMouseLeave={scheduleClose}
      >
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center justify-between h-16 lg:h-20">

            {/* Logo */}
            <a href="/" className="group transition-opacity">
              <img src={logoLmd300} srcSet={`${logoLmd300} 300w, ${logoLmd} 914w`} sizes="95px" alt={t.nav.logo} width={300} height={101} className="h-[32px] w-auto brightness-0 invert group-hover:opacity-70 transition-opacity duration-300" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-12">

              {/* Služby wrapper */}
              <div
                ref={wrapperRef}
                className="relative self-stretch flex items-center"
                onMouseEnter={() => {
                  clearCloseTimeout();
                  updateDropdownPos();
                  setIsDropdownOpen(true);
                }}
              >
                <div className="relative flex items-center gap-1.5">
                  <button
                    onClick={handleServicesClick}
                    className={`text-[15px] font-medium transition-colors duration-300 pb-1 relative ${
                      isActive("#sluzby") ? "text-white nav-underline-active" : "text-gray-300 hover:text-white nav-underline"
                    }`}
                  >
                    {servicesItem.label}
                  </button>
                  <button
                    onClick={() => {
                      updateDropdownPos();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                    className="text-gray-300 transition-all duration-300 hover:text-white p-0.5"
                  >
                    <ChevronDown
                      size={14}
                      className="transition-transform duration-200"
                      style={{ transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                    />
                  </button>
                </div>
              </div>

              {/* Regular nav items */}
              {regularNavItems.map((item) => {
                const isItemActive = isActive(item.href);
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`text-[15px] font-medium transition-colors duration-300 pb-1 relative ${
                      isItemActive ? "text-white nav-underline-active" : "text-gray-300 hover:text-white nav-underline"
                    }`}
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>

            {/* Language Switcher */}
            <div className="hidden lg:flex items-center gap-1 text-sm font-medium">
              <button
                onClick={() => setLanguage("cs")}
                className={`px-2 py-1 transition-colors duration-200 ${
                  language === "cs" ? "text-foreground" : "text-gray-500 hover:text-gray-300"
                }`}
              >
                CZ
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => setLanguage("en")}
                className={`px-2 py-1 transition-colors duration-200 ${
                  language === "en" ? "text-foreground" : "text-gray-500 hover:text-gray-300"
                }`}
              >
                EN
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-foreground p-2"
              aria-label={isMobileMenuOpen ? t.mobileMenu.close : t.mobileMenu.open}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>

        </div>
      </motion.header>

      {dropdownPortal}

      {/* Mobile menu: overlay + panel in a single portal so z-indices work correctly */}
      {isMobileMenuOpen && createPortal(
        <>
          {/* Overlay — closes menu on tap outside */}
          <div
            className="lg:hidden fixed inset-0 z-[55] bg-transparent"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Menu panel — above overlay */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed top-14 left-0 right-0 bg-black border-b border-border z-[60]"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className={`inline-flex items-center gap-1.5 text-lg font-medium transition-all duration-300 border-b-2 pb-1 self-start ${
                    isActive("#sluzby") ? "text-foreground border-white" : "text-gray-400 border-transparent hover:text-foreground"
                  }`}
                >
                  {servicesItem.label}
                  <ChevronDown
                    size={18}
                    className="transition-transform duration-200"
                    style={{ transform: isMobileServicesOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>
                <AnimatePresence>
                  {isMobileServicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-6 pt-3 flex flex-col gap-3">
                        {serviceDropdownItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <a
                              key={item.href}
                              href={item.href}
                              className="flex items-center gap-3 text-gray-400 hover:text-foreground transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <Icon size={16} style={{ color: item.color }} />
                              <span className="text-base">{item.label[language]}</span>
                            </a>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {regularNavItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={`inline-block text-lg font-medium transition-all duration-300 border-b-2 pb-1 self-start ${
                    isActive(item.href) ? "text-foreground border-white" : "text-gray-400 border-transparent hover:text-foreground"
                  }`}
                  onClick={(e) => {
                    handleNavClick(e, item.href);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {item.label}
                </a>
              ))}

              <div className="flex items-center justify-center gap-2 mt-4 text-lg font-medium">
                <button
                  onClick={() => { setLanguage("cs"); setIsMobileMenuOpen(false); }}
                  className={`px-3 py-2 transition-colors duration-200 ${
                    language === "cs" ? "text-foreground" : "text-gray-500"
                  }`}
                >
                  CZ
                </button>
                <span className="text-gray-600">|</span>
                <button
                  onClick={() => { setLanguage("en"); setIsMobileMenuOpen(false); }}
                  className={`px-3 py-2 transition-colors duration-200 ${
                    language === "en" ? "text-foreground" : "text-gray-500"
                  }`}
                >
                  EN
                </button>
              </div>
            </div>
          </motion.div>
        </>,
        document.body
      )}
    </>
  );
};
